import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { SME_ORGANIZATION_TYPE } from '../../../enum/smeApplication';
import { getCityStateByPostcodeThunk } from '../../../services/locationService/locationThunk';
import { phoneRegexPattern, emailRegexPattern } from '../../../utils/validation';
import { updateAccountDetailsSmeApplicationThunk } from '../../../services/collectionService/collectionThunk';
import { getAccountDetailsThunk } from '../../../services/collectionService/collectionThunk';

interface EditSmeCompanyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditSmeCompanyDetailsModal: React.FC<EditSmeCompanyDetailsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const { accountEnquiry } = useSelector((state: RootState) => state.collection);

  const data = accountEnquiry?.data?.accountDetails?.smeApplication;
  const organizationType = accountEnquiry?.data?.accountDetails?.organizationType;
  const accountDetailsId = accountEnquiry?.data?.accountDetails?.id;
  const smeDirectors = accountEnquiry?.data?.accountDetails?.smeDirector ?? [];
  const authorizedPerson = smeDirectors.find((director: any) => director.isAuthorizedPerson);

  useEffect(() => {
    if (data && isOpen) {
      const formValues: any = {
        // Disabled fields
        companyName: data?.companyName,
        businessOldRegistrationNo: data?.businessOldRegistrationNo,
        businessNewRegistrationNo: data?.businessNewRegistrationNo,
        countryOfIncorporation: data?.countryOfIncorporation,
        nameOfSeniorManagement: data?.nameOfSeniorManagement,
        natureOfBusiness: data?.natureOfBusiness,
        shareholdingStatus: data?.shareholdingStatus,
        registeredSsmAddress1: data?.registeredSsmAddress1,
        registeredSsmAddress2: data?.registeredSsmAddress2,
        registeredSsmAddress3: data?.registeredSsmAddress3,
        registeredSsmPostcode: data?.registeredSsmPostcode,
        registeredSsmCity: data?.registeredSsmCity,
        registeredSsmState: data?.registeredSsmState,

        // Editable fields
        companyEmail: data?.companyEmail,
        businessContactNo: data?.businessContactNo,
      };

      // Authorized person fields
      if (organizationType === SME_ORGANIZATION_TYPE.COMPANY && authorizedPerson) {
        formValues.authorisedPersonName = authorizedPerson?.directorName;
        formValues.authorisedPersonNric = authorizedPerson?.nric;
        formValues.authorisedPersonEmail = authorizedPerson?.personalEmail;
        formValues.authorisedPersonPhone = authorizedPerson?.phoneNo;
      }

      // For COMPANY organization type, show separate Business Address and Business Authorized Address
      if (organizationType === SME_ORGANIZATION_TYPE.COMPANY) {
        formValues.businessAddress1 = data?.businessAddress1;
        formValues.businessAddress2 = data?.businessAddress2;
        formValues.businessAddress3 = data?.businessAddress3;
        formValues.businessPostcode = data?.businessPostcode;
        formValues.businessCity = data?.businessCity;
        formValues.businessState = data?.businessState;
        formValues.businessAuthorisedAddress1 = data?.businessAuthorisedAddress1;
        formValues.businessAuthorisedAddress2 = data?.businessAuthorisedAddress2;
        formValues.businessAuthorisedAddress3 = data?.businessAuthorisedAddress3;
        formValues.businessAuthorisedPostcode = data?.businessAuthorisedPostcode;
        formValues.businessAuthorisedCity = data?.businessAuthorisedCity;
        formValues.businessAuthorisedState = data?.businessAuthorisedState;
      } else if (organizationType === SME_ORGANIZATION_TYPE.SOLE_PROP) {
        // For SOLE_PROP, Business Authorized Address uses businessAddress fields
        formValues.businessAddress1 = data?.businessAddress1;
        formValues.businessAddress2 = data?.businessAddress2;
        formValues.businessAddress3 = data?.businessAddress3;
        formValues.businessPostcode = data?.businessPostcode;
        formValues.businessCity = data?.businessCity;
        formValues.businessState = data?.businessState;
      } else {
        // For PARTNERSHIP and others, use businessAuthorisedAddress fields
        formValues.businessAuthorisedAddress1 = data?.businessAuthorisedAddress1;
        formValues.businessAuthorisedAddress2 = data?.businessAuthorisedAddress2;
        formValues.businessAuthorisedAddress3 = data?.businessAuthorisedAddress3;
        formValues.businessAuthorisedPostcode = data?.businessAuthorisedPostcode;
        formValues.businessAuthorisedCity = data?.businessAuthorisedCity;
        formValues.businessAuthorisedState = data?.businessAuthorisedState;
      }

      form.setFieldsValue(formValues);
    }
  }, [data, isOpen, form, organizationType, authorizedPerson]);

  const handleRegisteredSsmPostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value;
    if (postcode.length === 5 && /^\d+$/.test(postcode)) {
      dispatch(getCityStateByPostcodeThunk({ postcode }))
        .unwrap()
        .then((res) => {
          form.setFieldsValue({
            registeredSsmCity: res[0].post_office,
            registeredSsmState: res[0].state_code,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            registeredSsmCity: null,
            registeredSsmState: null,
          });
        });
    } else {
      form.setFieldsValue({
        registeredSsmCity: null,
        registeredSsmState: null,
      });
    }
  };

  const handleBusinessPostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value;
    if (postcode.length === 5 && /^\d+$/.test(postcode)) {
      dispatch(getCityStateByPostcodeThunk({ postcode }))
        .unwrap()
        .then((res) => {
          form.setFieldsValue({
            businessCity: res[0].post_office,
            businessState: res[0].state_code,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            businessCity: null,
            businessState: null,
          });
        });
    } else {
      form.setFieldsValue({
        businessCity: null,
        businessState: null,
      });
    }
  };

  const handleBusinessAuthorisedPostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value;
    if (postcode.length === 5 && /^\d+$/.test(postcode)) {
      dispatch(getCityStateByPostcodeThunk({ postcode }))
        .unwrap()
        .then((res) => {
          form.setFieldsValue({
            businessAuthorisedCity: res[0].post_office,
            businessAuthorisedState: res[0].state_code,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            businessAuthorisedCity: null,
            businessAuthorisedState: null,
          });
        });
    } else {
      form.setFieldsValue({
        businessAuthorisedCity: null,
        businessAuthorisedState: null,
      });
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);

      // Only send editable fields
      const editableFields: any = {
        companyEmail: values.companyEmail,
        businessContactNo: values.businessContactNo,
        registeredSsmAddress1: values.registeredSsmAddress1,
        registeredSsmAddress2: values.registeredSsmAddress2,
        registeredSsmAddress3: values.registeredSsmAddress3,
        registeredSsmPostcode: values.registeredSsmPostcode,
        registeredSsmCity: values.registeredSsmCity,
        registeredSsmState: values.registeredSsmState,
      };

      // Add Authorized Contact Person email and phone for COMPANY type
      if (organizationType === SME_ORGANIZATION_TYPE.COMPANY) {
        editableFields.emailAddress = values.authorisedPersonEmail;
        editableFields.applicantPhoneNo = values.authorisedPersonPhone;
      }

      // Add business address fields based on organization type
      if (organizationType === SME_ORGANIZATION_TYPE.COMPANY) {
        Object.assign(editableFields, {
          businessAddress1: values.businessAddress1,
          businessAddress2: values.businessAddress2,
          businessAddress3: values.businessAddress3,
          businessPostcode: values.businessPostcode,
          businessCity: values.businessCity,
          businessState: values.businessState,
          businessAuthorisedAddress1: values.businessAuthorisedAddress1,
          businessAuthorisedAddress2: values.businessAuthorisedAddress2,
          businessAuthorisedAddress3: values.businessAuthorisedAddress3,
          businessAuthorisedPostcode: values.businessAuthorisedPostcode,
          businessAuthorisedCity: values.businessAuthorisedCity,
          businessAuthorisedState: values.businessAuthorisedState,
        });
      } else if (organizationType === SME_ORGANIZATION_TYPE.SOLE_PROP) {
        Object.assign(editableFields, {
          businessAddress1: values.businessAddress1,
          businessAddress2: values.businessAddress2,
          businessAddress3: values.businessAddress3,
          businessPostcode: values.businessPostcode,
          businessCity: values.businessCity,
          businessState: values.businessState,
        });
      } else {
        Object.assign(editableFields, {
          businessAuthorisedAddress1: values.businessAuthorisedAddress1,
          businessAuthorisedAddress2: values.businessAuthorisedAddress2,
          businessAuthorisedAddress3: values.businessAuthorisedAddress3,
          businessAuthorisedPostcode: values.businessAuthorisedPostcode,
          businessAuthorisedCity: values.businessAuthorisedCity,
          businessAuthorisedState: values.businessAuthorisedState,
        });
      }

      await dispatch(
        updateAccountDetailsSmeApplicationThunk({
          id: accountDetailsId,
          payload: editableFields,
        })
      ).unwrap();

      // Refresh account details
      await dispatch(
        getAccountDetailsThunk({
          agreementNo: accountEnquiry?.data?.accountDetails?.agreementNo,
        })
      ).unwrap();

      onClose();
      form.resetFields();
      message.success('Updated successfully');
    } catch (error: any) {
      message.error(error?.message || 'Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="Edit SME Customer Details"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>
          Save
        </Button>,
      ]}
      width={1200}
      className="overflow-auto"
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
        {/* Company Details Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Company Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="companyName" label="Company Name">
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="companyEmail"
              label="Company Email"
              rules={[
                { required: true, message: 'Company Email is Required' },
                { pattern: emailRegexPattern, message: 'Email is Invalid' },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="businessOldRegistrationNo" label="Business Old Registration No.">
              <Input disabled />
            </Form.Item>
            <Form.Item name="businessNewRegistrationNo" label="Business New Registration No.">
              <Input disabled />
            </Form.Item>
            <Form.Item name="countryOfIncorporation" label="Country of Incorporation/Registration">
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="businessContactNo"
              label="Business Contact No."
              rules={[
                { required: true, message: 'Business Contact No is Required' },
                { pattern: phoneRegexPattern, message: 'Invalid Phone No' },
              ]}
            >
              <Input />
            </Form.Item>
          </div>
        </div>

        {/* Management Information Section */}
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="nameOfSeniorManagement" label="Name of Senior Management">
              <Input disabled />
            </Form.Item>
            <Form.Item name="natureOfBusiness" label="Nature of Business">
              <Input disabled />
            </Form.Item>
            {organizationType === SME_ORGANIZATION_TYPE.COMPANY && (
              <Form.Item name="shareholdingStatus" label="Shareholding Status">
                <Input disabled />
              </Form.Item>
            )}
          </div>
        </div>

        {/* Registered Address (SSM) */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Registered Address (SSM)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="registeredSsmAddress1"
              label="Address Line 1"
              rules={[{ required: true, message: 'Registered Address Line 1 is Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="registeredSsmAddress2" label="Address Line 2">
              <Input />
            </Form.Item>
            <Form.Item name="registeredSsmAddress3" label="Address Line 3">
              <Input />
            </Form.Item>
            <Form.Item
              name="registeredSsmPostcode"
              label="Postcode"
              rules={[
                { required: true, message: 'Postcode is Required' },
                { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
              ]}
            >
              <Input onChange={handleRegisteredSsmPostcodeChange} maxLength={5} />
            </Form.Item>
            <Form.Item
              name="registeredSsmCity"
              label="City"
              rules={[{ required: true, message: 'City is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
            <Form.Item
              name="registeredSsmState"
              label="State"
              rules={[{ required: true, message: 'State is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
          </div>
        </div>

        {/* Business Authorized Address */}
        {organizationType === SME_ORGANIZATION_TYPE.SOLE_PROP ? (
          // For SOLE_PROP, show as Business Authorized Address but use businessAddress fields
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Business Authorized Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="businessAddress1"
                label="Address Line 1"
                rules={[
                  { required: true, message: 'Business Authorized Address Line 1 is Required' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="businessAddress2" label="Address Line 2">
                <Input />
              </Form.Item>
              <Form.Item name="businessAddress3" label="Address Line 3">
                <Input />
              </Form.Item>
              <Form.Item
                name="businessPostcode"
                label="Postcode"
                rules={[
                  { required: true, message: 'Postcode is Required' },
                  { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
                ]}
              >
                <Input onChange={handleBusinessPostcodeChange} maxLength={5} />
              </Form.Item>
              <Form.Item
                name="businessCity"
                label="City"
                rules={[{ required: true, message: 'City is Required' }]}
              >
                <Input disabled={true} />
              </Form.Item>
              <Form.Item
                name="businessState"
                label="State"
                rules={[{ required: true, message: 'State is Required' }]}
              >
                <Input disabled={true} />
              </Form.Item>
            </div>
          </div>
        ) : (
          // For COMPANY and PARTNERSHIP, use businessAuthorisedAddress fields
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Business Authorized Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="businessAuthorisedAddress1"
                label="Address Line 1"
                rules={[
                  { required: true, message: 'Business Authorized Address Line 1 is Required' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="businessAuthorisedAddress2" label="Address Line 2">
                <Input />
              </Form.Item>
              <Form.Item name="businessAuthorisedAddress3" label="Address Line 3">
                <Input />
              </Form.Item>
              <Form.Item
                name="businessAuthorisedPostcode"
                label="Postcode"
                rules={[
                  { required: true, message: 'Postcode is Required' },
                  { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
                ]}
              >
                <Input onChange={handleBusinessAuthorisedPostcodeChange} maxLength={5} />
              </Form.Item>
              <Form.Item
                name="businessAuthorisedCity"
                label="City"
                rules={[{ required: true, message: 'City is Required' }]}
              >
                <Input disabled={true} />
              </Form.Item>
              <Form.Item
                name="businessAuthorisedState"
                label="State"
                rules={[{ required: true, message: 'State is Required' }]}
              >
                <Input disabled={true} />
              </Form.Item>
            </div>
          </div>
        )}

        {/* Business Address - Only for COMPANY organization type */}
        {organizationType === SME_ORGANIZATION_TYPE.COMPANY && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Business Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="businessAddress1"
                label="Address Line 1"
                rules={[{ required: true, message: 'Business Address Line 1 is Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="businessAddress2" label="Address Line 2">
                <Input />
              </Form.Item>
              <Form.Item name="businessAddress3" label="Address Line 3">
                <Input />
              </Form.Item>
              <Form.Item
                name="businessPostcode"
                label="Postcode"
                rules={[
                  { required: true, message: 'Postcode is Required' },
                  { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
                ]}
              >
                <Input onChange={handleBusinessPostcodeChange} maxLength={5} />
              </Form.Item>
              <Form.Item
                name="businessCity"
                label="City"
                rules={[{ required: true, message: 'City is Required' }]}
              >
                <Input disabled={true} />
              </Form.Item>
              <Form.Item
                name="businessState"
                label="State"
                rules={[{ required: true, message: 'State is Required' }]}
              >
                <Input disabled={true} />
              </Form.Item>
            </div>
          </div>
        )}

        {/* Authorized Person Details - Only for COMPANY */}
        {organizationType === SME_ORGANIZATION_TYPE.COMPANY && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Authorized Contact Person</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item name="authorisedPersonName" label="Authorised Contact Person">
                <Input disabled />
              </Form.Item>
              <Form.Item name="authorisedPersonNric" label="Authorised Contact Person's NRIC">
                <Input disabled />
              </Form.Item>
              <Form.Item
                name="authorisedPersonEmail"
                label="Authorised Contact Person's Email"
                rules={[
                  { required: true, message: "Authorised Contact Person's Email is Required" },
                  { pattern: emailRegexPattern, message: 'Email is Invalid' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="authorisedPersonPhone"
                label="Authorised Contact Number"
                rules={[
                  { required: true, message: 'Authorised Contact Number is Required' },
                  { pattern: phoneRegexPattern, message: 'Invalid Phone No' },
                ]}
              >
                <Input />
              </Form.Item>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};
